import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, tap, map } from 'rxjs';
import { SelectOption, DepartmentNode, EmploymentTypeMetadata, MetadataResponse } from '../models/metadata.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  private http = inject(HttpClient);
  private readonly apiUrl = `/assets/data/metadata.json`;

  constructor() {}

  // Raw Data Signals
  private _rolesByDepartment = signal<Record<string, string[]>>({});
  private _departments = signal<DepartmentNode[]>([]);
  private _employmentTypes = signal<string[]>([]);
  private _statuses = signal<string[]>([]);
  private _workModes = signal<string[]>([]);

  // Computed Options for UI Components
  // Returns a flattened list of all unique roles for global filters/search
  readonly roleOptions = computed<SelectOption<string>[]>(() => {
    const rolesMap = this._rolesByDepartment();
    const allRolesSet = new Set<string>();
    Object.values(rolesMap).forEach(roles => roles.forEach(r => allRolesSet.add(r)));
    return Array.from(allRolesSet).sort().map(role => ({ label: role, value: role }));
  });

  readonly employmentTypeOptions = computed<SelectOption<string>[]>(() =>
    this._employmentTypes().map(type => {
      // Map descriptive backend names to legacy short keys to maintain form logic (e.g., 'CT' for Contract End Date)
      const keyMap: Record<string, string> = {
        'Full-time': 'FT',
        'Part-time': 'PT',
        'Contract': 'CT',
        'Intern': 'Intern'
      };
      return { label: type, value: keyMap[type] || type };
    })
  );

  readonly departmentTree = computed<DepartmentNode[]>(() => this._departments());

  readonly statusOptions = computed<SelectOption<string>[]>(() =>
    this._statuses().map(status => ({ label: status, value: status }))
  );

  readonly workModeOptions = computed<SelectOption<string>[]>(() =>
    this._workModes().map(mode => ({ label: mode, value: mode }))
  );



  // Filter-specific Options (with "All" option)
  readonly roleFilterOptions = computed<SelectOption<string | null>[]>(() => [
    { label: 'All Roles', value: null },
    ...this.roleOptions()
  ]);

  readonly typeFilterOptions = computed<SelectOption<string | null>[]>(() => [
    { label: 'All Types', value: null },
    ...this.employmentTypeOptions()
  ]);

  readonly statusFilterOptions = computed<SelectOption<string | null>[]>(() => [
    { label: 'All Status', value: null },
    ...this.statusOptions()
  ]);

  /**
   * Initializes the metadata from the backend API.
   * returns a Promise so it can be used in APP_INITIALIZER.
   */
  loadMetadata(): Promise<void> {
    return lastValueFrom(
      this.http.get<MetadataResponse>(this.apiUrl).pipe(
        tap(data => {
          this._rolesByDepartment.set(data.rolesByDepartment || {});
          
          let rawDeps = data.departments || [];
          // Rule A: Remove 'corp' root node and promote its children
          if (rawDeps.length === 1 && rawDeps[0].key === 'corp') {
            rawDeps = rawDeps[0].children || [];
          }
          
          // Rule B: Apply recursive formatting (expanded, selectable)
          this._departments.set(this.formatTree(rawDeps));

          this._workModes.set(data.workModes || []);
          this._employmentTypes.set(data.employmentTypes || []);
          this._statuses.set(data.statuses || []);
        }),
        map(() => void 0) // Explicitly return void
      )
    ).catch(err => {
      if (environment.environmentName !== 'DEV') {
        console.error('Failed to load metadata from API:', err);
        throw err;
      }
      console.warn('Failed to load metadata from API, using local fallbacks.', err);
      // Fallback data to prevent app from breaking
      this._rolesByDepartment.set({
        'Engineering': ['Software Engineer', 'Senior Engineer', 'Lead Engineer', 'QA Engineer'],
        'HR': ['HR Manager', 'Recruiter', 'HR Specialist'],
        'Sales': ['Sales Executive', 'Account Manager']
      });
      this._departments.set([
        { label: 'Engineering', key: 'Engineering', data: 'Engineering', expanded: true, selectable: true },
        { label: 'HR', key: 'HR', data: 'HR', expanded: true, selectable: true },
        { label: 'Sales', key: 'Sales', data: 'Sales', expanded: true, selectable: true }
      ]);
      this._workModes.set(['On-site', 'Remote', 'Hybrid']);
      this._employmentTypes.set(['Full-time', 'Part-time', 'Contract', 'Intern']);
      this._statuses.set(['Active', 'Inactive', 'On Leave']);
    });
  }

  /**
   * Returns the available roles for a specific department key.
   * @param deptKey The key/data of the department
   */
  getRolesForDepartment(deptKey: string): SelectOption<string>[] {
    const roles = this._rolesByDepartment()[deptKey] || [];
    return roles.map(role => ({ label: role, value: role }));
  }

  /**
   * Recursively formats the tree structure for UX improvements.
   * Sets expanded: true for all nodes and ensures only leaf nodes are selectable.
   */
  private formatTree(nodes: DepartmentNode[]): DepartmentNode[] {
    return nodes.map(node => {
      const hasChildren = node.children && node.children.length > 0;
      return {
        ...node,
        expanded: true,
        selectable: !hasChildren,
        children: hasChildren ? this.formatTree(node.children!) : undefined
      };
    });
  }

  /**
   * Dynamically adds a new role to the metadata list if it doesn't already exist.
   * This is useful for handling roles coming from the database that aren't in the static list.
   * @param role The job title to add
   * @param deptKey The department key to associate the role with
   */
  addRoleOption(role: string, deptKey: string) {
    if (!role || !deptKey) return;

    this._rolesByDepartment.update(map => {
      const deptRoles = map[deptKey] || [];
      if (!deptRoles.some(r => r.toLowerCase() === role.toLowerCase())) {
        return { ...map, [deptKey]: [...deptRoles, role] };
      }
      return map;
    });
  }
}
